import AddRounded from '@mui/icons-material/AddRounded';
import AlarmRounded from '@mui/icons-material/AlarmRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import EditOutlined from '@mui/icons-material/EditOutlined';
import ForwardToInboxOutlined from '@mui/icons-material/ForwardToInboxOutlined';
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined';
import PlayArrowRounded from '@mui/icons-material/PlayArrowRounded';
import ReceiptLongRounded from '@mui/icons-material/ReceiptLongRounded';
import RequestQuoteOutlined from '@mui/icons-material/RequestQuoteOutlined';
import WorkspacePremiumOutlined from '@mui/icons-material/WorkspacePremiumOutlined';
import LocalShippingOutlined from '@mui/icons-material/LocalShippingOutlined';
import {
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  MenuItem,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { api, unwrap } from '../../../api/client';
import { useAuth } from '../../../auth/AuthProvider';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { DataTable } from '../../../components/DataTable';
import { EmptyState, PageHeader } from '../../../components/states';
import { StatusChip } from '../../../components/StatusChip';
import { dateTime, relativeTime } from '../../../lib/format';
import { useNotify } from '../../../lib/notify';
import { useShop } from '../../../shop/ShopProvider';
import { PageTabs } from '../components/UrlTabs';
import { IconBadge, NoAccess, QueryError, type Tone } from '../components/ui';
import { useReportParams } from '../components/useReportParams';
import { useUrlTab } from '../components/useUrlTab';
import {
  CHANNELS,
  describeTiming,
  KINDS,
  type ReminderKind,
  type ReminderRule,
} from '../lib/reminderRule';
import { NotificationList } from './NotificationList';
import { RuleDialog } from './RuleDialog';

type TabKey = 'rules' | 'notifications' | 'messages';

const KIND_ICON: Record<ReminderKind, { icon: ReactNode; tone: Tone }> = {
  INVOICE_DUE_SOON: { icon: <ReceiptLongRounded />, tone: 'info' },
  INVOICE_OVERDUE: { icon: <ReceiptLongRounded />, tone: 'error' },
  BILL_DUE_SOON: { icon: <RequestQuoteOutlined />, tone: 'info' },
  BILL_OVERDUE: { icon: <RequestQuoteOutlined />, tone: 'error' },
  LOW_STOCK: { icon: <Inventory2Outlined />, tone: 'warning' },
  QUOTATION_EXPIRING: { icon: <AlarmRounded />, tone: 'warning' },
  PO_OVERDUE: { icon: <LocalShippingOutlined />, tone: 'warning' },
  SUBSCRIPTION_EXPIRING: { icon: <WorkspacePremiumOutlined />, tone: 'primary' },
};

export function RemindersPage() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const notify = useNotify();
  const canView = can('reminders.view');
  const canManage = can('reminders.manage');
  const [tab, setTab] = useUrlTab<TabKey>([
    { value: 'rules', label: 'Rules', show: canView },
    { value: 'notifications', label: 'My notifications' },
    { value: 'messages', label: 'Message log', show: canView },
  ]);
  const [editing, setEditing] = useState<ReminderRule | null | undefined>(undefined);
  const run = useMutation({
    mutationFn: () => unwrap(api.POST('/reminders/run', { body: {} })),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['notifications'] });
      void qc.invalidateQueries({ queryKey: ['reminder-rules'] });
      void qc.invalidateQueries({ queryKey: ['reminder-messages'] });
      if (r.error) notify.error(new Error(r.error));
      else
        notify.success(
          `Checked ${String(r.rulesEvaluated)} rules · ${String(r.notificationsCreated)} notifications, ${String(r.messagesQueued)} messages`,
        );
    },
    onError: (e) => notify.error(e),
  });

  return (
    <>
      <PageHeader
        title="Reminders"
        subtitle="Automatic nudges about due invoices, supplier bills, low stock and more."
        actions={
          canManage &&
          tab === 'rules' && (
            <>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<PlayArrowRounded />}
                loading={run.isPending}
                onClick={() => run.mutate()}
                sx={{ borderColor: 'divider', bgcolor: 'background.paper' }}
              >
                Run now
              </Button>
              <Button
                variant="contained"
                startIcon={<AddRounded />}
                onClick={() => setEditing(null)}
              >
                New reminder
              </Button>
            </>
          )
        }
      />
      <PageTabs
        value={tab}
        onChange={setTab}
        tabs={[
          { value: 'rules', label: 'Rules', show: canView },
          { value: 'notifications', label: 'My notifications' },
          { value: 'messages', label: 'Message log', show: canView },
        ]}
      />
      {tab === 'rules' &&
        (canView ? (
          <RulesTab onEdit={setEditing} onNew={() => setEditing(null)} />
        ) : (
          <NoAccess what="reminder rules" />
        ))}
      {tab === 'notifications' && <NotificationList />}
      {tab === 'messages' && <MessagesTab />}
      <RuleDialog
        open={editing !== undefined}
        rule={editing ?? null}
        onClose={() => setEditing(undefined)}
      />
    </>
  );
}

function RulesTab({ onEdit, onNew }: { onEdit: (r: ReminderRule) => void; onNew: () => void }) {
  const { can } = useAuth();
  const { format } = useShop();
  const qc = useQueryClient();
  const notify = useNotify();
  const canManage = can('reminders.manage');
  const [deleting, setDeleting] = useState<ReminderRule | null>(null);
  const q = useQuery({
    queryKey: ['reminder-rules'],
    queryFn: () => unwrap(api.GET('/reminder-rules')),
  });
  const toggle = useMutation({
    mutationFn: (r: ReminderRule) =>
      unwrap(
        api.PATCH('/reminder-rules/{id}', {
          params: { path: { id: r.id } },
          body: { isActive: !r.isActive, version: r.version },
        }),
      ),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['reminder-rules'] });
      notify.success(r.isActive ? `“${r.name}” is on` : `“${r.name}” is paused`);
    },
    onError: (e) => notify.error(e),
  });

  if (q.error) return <QueryError error={q.error} onRetry={() => void q.refetch()} />;
  if (q.isLoading)
    return (
      <Stack spacing={1.5}>
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} variant="rounded" height={88} />
        ))}
      </Stack>
    );
  const rules = q.data?.data ?? [];
  if (rules.length === 0)
    return (
      <Card>
        <EmptyState
          icon={<AlarmRounded />}
          title="No reminders yet"
          body="Get told before invoices fall due, when stock runs low, or when a quotation is about to expire."
          action={canManage ? { label: 'Create a reminder', onClick: onNew } : undefined}
        />
      </Card>
    );
  return (
    <>
      <Stack spacing={1.5}>
        {rules.map((r) => {
          const k = KIND_ICON[r.kind];
          return (
            <Card key={r.id} sx={{ opacity: r.isActive ? 1 : 0.7 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{ p: 2.25, alignItems: { sm: 'center' } }}
              >
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{ flex: 1, minWidth: 0, alignItems: 'center' }}
                >
                  <IconBadge icon={k.icon} tone={r.isActive ? k.tone : 'neutral'} size={44} />
                  <Box sx={{ minWidth: 0 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ alignItems: 'center', flexWrap: 'wrap' }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 650 }}>
                        {r.name}
                      </Typography>
                      {!r.isActive && <Chip size="small" label="Paused" />}
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {KINDS[r.kind].label} · {describeTiming(r)}
                    </Typography>
                    <Stack
                      direction="row"
                      sx={{ gap: 0.75, mt: 0.75, flexWrap: 'wrap', alignItems: 'center' }}
                    >
                      {r.channels.map((c) => (
                        <Chip
                          key={c}
                          size="small"
                          variant="outlined"
                          label={CHANNELS.find((x) => x.value === c)?.label ?? c}
                          sx={{ height: 22, fontSize: 11.5 }}
                        />
                      ))}
                      {r.notifyContact && (
                        <Chip
                          size="small"
                          color="primary"
                          variant="outlined"
                          label="Messages contact"
                          sx={{ height: 22, fontSize: 11.5 }}
                        />
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                        {r.lastEvaluatedAt ? (
                          <Tooltip title={dateTime(r.lastEvaluatedAt, format)}>
                            <span>Last checked {relativeTime(r.lastEvaluatedAt)}</span>
                          </Tooltip>
                        ) : (
                          'Not checked yet'
                        )}
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>
                {canManage && (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{ alignItems: 'center', alignSelf: { xs: 'flex-end', sm: 'center' } }}
                  >
                    <Tooltip title={r.isActive ? 'Pause' : 'Turn on'}>
                      <Switch
                        checked={r.isActive}
                        disabled={toggle.isPending && toggle.variables.id === r.id}
                        onChange={() => toggle.mutate(r)}
                        slotProps={{
                          input: { 'aria-label': `${r.isActive ? 'Pause' : 'Turn on'} ${r.name}` },
                        }}
                      />
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton aria-label={`Edit ${r.name}`} onClick={() => onEdit(r)}>
                        <EditOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton aria-label={`Delete ${r.name}`} onClick={() => setDeleting(r)}>
                        <DeleteOutlineRounded fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                )}
              </Stack>
            </Card>
          );
        })}
      </Stack>
      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete this reminder?"
        body={`“${deleting?.name ?? ''}” will stop sending reminders. Past notifications and messages are kept.`}
        confirmLabel="Delete"
        danger
        onClose={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting) return;
          try {
            await unwrap(
              api.DELETE('/reminder-rules/{id}', {
                params: { path: { id: deleting.id }, query: { version: deleting.version } },
              }),
            );
            void qc.invalidateQueries({ queryKey: ['reminder-rules'] });
            notify.success('Reminder deleted');
          } catch (e) {
            notify.error(e);
          }
        }}
      />
    </>
  );
}

const MESSAGE_STATUSES = [
  'QUEUED',
  'SENDING',
  'SENT',
  'DELIVERED',
  'FAILED',
  'NOT_CONFIGURED',
] as const;
type MessageStatus = (typeof MESSAGE_STATUSES)[number];
type MessageChannel = Exclude<(typeof CHANNELS)[number]['value'], 'IN_APP'>;

function MessagesTab() {
  const p = useReportParams();
  const { format } = useShop();
  const statusParam = p.get('status');
  const channelParam = p.get('channel');
  const status = MESSAGE_STATUSES.find((s) => s === statusParam);
  const channel = (['EMAIL', 'SMS', 'WHATSAPP'] as const).find<MessageChannel>(
    (c): c is MessageChannel => c === channelParam,
  );
  const query = { page: p.page, pageSize: p.pageSize, status, channel };
  const q = useQuery({
    queryKey: ['reminder-messages', query],
    queryFn: () => unwrap(api.GET('/reminders/messages', { params: { query } })),
    placeholderData: keepPreviousData,
  });
  const tone = (s: MessageStatus) =>
    s === 'FAILED'
      ? 'VOID'
      : s === 'NOT_CONFIGURED'
        ? 'DRAFT'
        : s === 'QUEUED' || s === 'SENDING'
          ? 'OPEN'
          : 'COMPLETED';
  return (
    <>
      {q.error && <QueryError error={q.error} onRetry={() => void q.refetch()} />}
      <DataTable
        loading={q.isLoading}
        rows={q.data?.data}
        rowKey={(m) => m.id}
        page={q.data?.page}
        onPageChange={p.setPage}
        onPageSizeChange={p.setPageSize}
        toolbar={
          <>
            <TextField
              select
              size="small"
              fullWidth={false}
              label="Status"
              value={status ?? ''}
              onChange={(e) => p.update({ status: e.target.value || null })}
              sx={{ minWidth: 170 }}
              slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
            >
              <MenuItem value="">All statuses</MenuItem>
              {MESSAGE_STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s === 'NOT_CONFIGURED'
                    ? 'Not configured'
                    : s.charAt(0) + s.slice(1).toLowerCase()}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              fullWidth={false}
              label="Channel"
              value={channel ?? ''}
              onChange={(e) => p.update({ channel: e.target.value || null })}
              sx={{ minWidth: 150 }}
              slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
            >
              <MenuItem value="">All channels</MenuItem>
              {CHANNELS.filter((c) => c.value !== 'IN_APP').map((c) => (
                <MenuItem key={c.value} value={c.value}>
                  {c.label}
                </MenuItem>
              ))}
            </TextField>
          </>
        }
        empty={
          <EmptyState
            icon={<ForwardToInboxOutlined />}
            title="No messages sent"
            body="Email, SMS and WhatsApp reminders to customers and staff are logged here."
          />
        }
        columns={[
          {
            key: 'when',
            header: 'When',
            width: 140,
            render: (m) => dateTime(m.sentAt ?? m.createdAt, format),
          },
          {
            key: 'to',
            header: 'To',
            render: (m) => (
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {m.toAddress}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {CHANNELS.find((c) => c.value === m.channel)?.label} ·{' '}
                  {m.recipientType === 'CONTACT'
                    ? 'Customer / supplier'
                    : m.recipientType === 'USER'
                      ? 'Staff'
                      : 'Invitee'}
                </Typography>
              </Box>
            ),
          },
          {
            key: 'msg',
            header: 'Message',
            hideBelow: 'md',
            render: (m) => (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  maxWidth: 420,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {m.subject ? <b>{m.subject} — </b> : null}
                {m.body}
              </Typography>
            ),
          },
          {
            key: 'status',
            header: 'Status',
            render: (m) => (
              <Tooltip title={m.error ?? ''}>
                <span>
                  <StatusChip
                    status={tone(m.status)}
                    label={
                      m.status === 'NOT_CONFIGURED'
                        ? 'Not configured'
                        : m.status.charAt(0) + m.status.slice(1).toLowerCase()
                    }
                  />
                </span>
              </Tooltip>
            ),
          },
        ]}
      />
    </>
  );
}
